import { useEffect, useState } from "react";
import { Plus, Copy, Check, Trash2, RefreshCcw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [form, setForm] = useState({ count: 1, class_hint: "", board_hint: "", note: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enrollment-codes");
      setCodes(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        count: parseInt(form.count) || 1,
        class_hint: form.class_hint || null,
        board_hint: form.board_hint || null,
        note: form.note || null,
      };
      const { data } = await api.post("/admin/enrollment-codes", payload);
      toast.success(`Generated ${data.length} code${data.length > 1 ? "s" : ""}`);
      setForm({ count: 1, class_hint: "", board_hint: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const copy = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this unused code?")) return;
    try {
      await api.delete(`/admin/enrollment-codes/${id}`);
      toast.success("Code deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const unused = codes.filter(c => !c.used_by_student_id);
  const used = codes.filter(c => c.used_by_student_id);

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl font-800 text-[#0B192C]">Enrollment Codes</h1>
          <p className="text-slate-600 mt-1">Generate codes and share them with new students so they can register.</p>
        </div>
        <button onClick={load} className="vk-btn-dark text-sm"><RefreshCcw size={14} /> Refresh</button>
      </div>

      <form onSubmit={generate} data-testid="generate-code-form" className="rounded-lg border border-slate-200 bg-white p-6 grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
        <div>
          <Label>How many</Label>
          <Input type="number" min={1} max={100} value={form.count} onChange={(e) => setForm(f => ({ ...f, count: e.target.value }))} data-testid="gen-count" className="mt-1" />
        </div>
        <div>
          <Label>Class (optional)</Label>
          <Select value={form.class_hint} onValueChange={(v) => setForm(f => ({ ...f, class_hint: v === "none" ? "" : v }))}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Any</SelectItem>
              {["VI","VII","VIII","IX","X"].map(c => (<SelectItem key={c} value={c}>Class {c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Board (optional)</Label>
          <Select value={form.board_hint} onValueChange={(v) => setForm(f => ({ ...f, board_hint: v === "none" ? "" : v }))}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Any</SelectItem>
              <SelectItem value="CBSE">CBSE</SelectItem>
              <SelectItem value="ICSE">ICSE</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-1">
          <Label>Note (optional)</Label>
          <Input value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Batch A intake" className="mt-1" />
        </div>
        <button type="submit" disabled={creating} data-testid="gen-submit" className="vk-btn-primary justify-center">
          <Plus size={16} /> {creating ? "Generating…" : "Generate"}
        </button>
      </form>

      <section>
        <h2 className="font-display text-xl font-800 text-[#0B192C] mb-3">Unused ({unused.length})</h2>
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading…</div>
          ) : unused.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No unused codes. Generate some above.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {unused.map(c => (
                <li key={c.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`code-${c.id}`}>
                  <div className="flex items-center gap-2 flex-1">
                    <KeyRound size={16} className="text-[#FF6B35]" />
                    <span className="font-mono text-lg font-semibold tracking-widest text-[#0B192C]">{c.code}</span>
                    <button onClick={() => copy(c.code, c.id)} className="text-slate-400 hover:text-[#0B192C]" title="Copy" data-testid={`copy-${c.id}`}>
                      {copiedId === c.id ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {c.class_hint && <span className="vk-chip">Class {c.class_hint}</span>}
                    {c.board_hint && <span className="vk-chip">{c.board_hint}</span>}
                    {c.note && <span className="italic">{c.note}</span>}
                    <span>· created {new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-500" title="Delete" data-testid={`del-code-${c.id}`}>
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {used.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-800 text-[#0B192C] mb-3">Used ({used.length})</h2>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <ul className="divide-y divide-slate-100 text-sm">
              {used.map(c => (
                <li key={c.id} className="p-4 flex items-center gap-3 text-slate-600">
                  <KeyRound size={14} />
                  <span className="font-mono">{c.code}</span>
                  <span className="text-xs text-slate-400">used {c.used_at ? new Date(c.used_at).toLocaleDateString() : ""}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </>
  );
}
